import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import { useSubscription } from '../../context/SubscriptionContext';
import UpgradePromptModal from '../subscription/UpgradePromptModal';
import UsageIndicator from '../subscription/UsageIndicator';
import { useNavigate } from 'react-router-dom';
import '../../styles/InviteOwnerModal.css';

const InviteOwnerModal = ({ isOpen, onClose }) => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [limitData, setLimitData] = useState(null);
    const [isCheckingLimit, setIsCheckingLimit] = useState(false);

    const navigate = useNavigate();

    const {
        canAddOwner,
        usageData,
        refreshUsageData
    } = useSubscription();

    // Check limits when modal opens
    useEffect(() => {
        if (isOpen) {
            checkOwnerLimit();
        }
    }, [isOpen]);

    const checkOwnerLimit = async () => {
        setIsCheckingLimit(true);
        try {
            const result = await canAddOwner();
            if (!result.canAddOwner) {
                setLimitData(result);
            } else {
                setLimitData(null);
            }
        } catch (err) {
            console.error('Error checking owner limit:', err);
        } finally {
            setIsCheckingLimit(false);
        }
    };

    // Reset states when modal closes
    const handleClose = () => {
        setEmail('');
        setError('');
        setSuccess('');
        setLimitData(null);
        setShowUpgradeModal(false);
        onClose();
    };

    const handleUpgradeClick = () => {
        handleClose();
        navigate('/subscription/plans');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        // Check limit before submitting
        const limitCheck = await canAddOwner();
        if (!limitCheck.canAddOwner) {
            setLimitData(limitCheck);
            setShowUpgradeModal(true);
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await axios.post('/api/v1/auth/invite/owner', { email }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            const invitationToken = response.data; // Token from backend
            setSuccess('Zaproszenie wysłane pomyślnie!');

            // Refresh usage data after successful invite
            refreshUsageData();

            // Show success message and close after delay
            setTimeout(() => {
                handleClose();
            }, 3000);

        } catch (err) {
            // Check if this is an owner limit error (403)
            if (err.response?.status === 403 && err.response?.data?.errors?.upgradeRequired) {
                const errorData = err.response.data.errors;
                setLimitData({
                    currentOwners: parseInt(errorData.currentOwners) || 0,
                    maxOwners: parseInt(errorData.maxOwners) || 0,
                    planName: errorData.planName || 'Nieznany',
                    upgradeRequired: true
                });
                setShowUpgradeModal(true);
            } else if (err.response?.status === 409) {
                setError('Ten adres email jest już używany.');
            } else {
                setError('Wysłanie zaproszenia nie powiodło się. Proszę spróbować ponownie.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Don't render anything if modal is not open
    if (!isOpen) return null;

    // Show upgrade modal if limit is reached
    if (showUpgradeModal && limitData) {
        return (
            <UpgradePromptModal
                isOpen={true}
                onClose={() => setShowUpgradeModal(false)}
                currentOwners={limitData.currentOwners}
                maxOwners={limitData.maxOwners}
                planName={limitData.planName}
                suggestedPlanName={limitData.suggestedUpgradePlanName}
                availableUpgrades={limitData.availableUpgrades}
            />
        );
    }

    // Show limit warning if at limit (but modal not shown yet)
    const isAtLimit = limitData && !limitData.canAddOwner;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Zaproś właściciela</h2>
                </div>

                {/* Show usage indicator */}
                {usageData && (
                    <div className="usage-section">
                        <UsageIndicator
                            currentOwners={usageData.currentOwners}
                            maxOwners={usageData.maxOwners}
                            planName={usageData.planName}
                            showUpgradeLink={false}
                            compact={true}
                        />
                    </div>
                )}

                {/* Show limit reached message */}
                {isAtLimit && (
                    <div className="limit-reached-message">
                        <p>Osiągnięto limit właścicieli dla Twojego planu.</p>
                        <button
                            type="button"
                            className="upgrade-button"
                            onClick={handleUpgradeClick}
                        >
                            Ulepsz plan
                        </button>
                    </div>
                )}

                {/* Only show form if not at limit */}
                {!isAtLimit && (
                    <form className="invite-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <input
                                id="email"
                                type="email"
                                placeholder="Adres email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>

                        {error && <p className="error-message">{error}</p>}
                        {success && <p className="success-message">{success}</p>}

                        <div className="form-actions">
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={handleClose}
                            >
                                Anuluj
                            </button>
                            <button
                                type="submit"
                                className="submit-button"
                                disabled={isSubmitting || isCheckingLimit}
                            >
                                {isSubmitting ? 'Wysyłanie...' : isCheckingLimit ? 'Sprawdzanie...' : 'Zaproś'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Show close button if at limit */}
                {isAtLimit && (
                    <div className="form-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={handleClose}
                        >
                            Zamknij
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InviteOwnerModal;
